import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, User, MessageCircle, Send } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { WA_NUMBER_1, WA_GROUP_MSG, WA_INDIVIDUAL_MSG, waLink, trackEvent } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Coaching = () => {
  const [form, setForm] = useState({ name: "", phone: "", interest: "book", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received! We'll reach out to you soon.");
      setForm({ name: "", phone: "", interest: "book", message: "" });
    } catch (err) {
      toast.error("Could not send your enquiry. Please use WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 placeholder:font-normal placeholder:text-slate-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 transition-[border-color,box-shadow] duration-200";

  return (
    <section id="coaching" data-testid="coaching-section" className="py-20 lg:py-28 bg-brand-light scroll-mt-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeading
          align="center"
          eyebrow="IQMIZE Coaching"
          title="Go further with guided mentoring."
          description="Many families start with the ₹49 book — then choose guided mentoring when they're ready for the full 7-day experience."
        />

        <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto" data-testid="coaching-cards">
          <Reveal>
            <div data-testid="group-mentoring-card" className="h-full rounded-3xl border-2 border-brand/30 bg-white p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.05)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.10)]">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-light text-brand p-3">
                <Users size={26} strokeWidth={1.8} />
              </div>
              <p className="mt-6 text-xs uppercase tracking-[0.2em] font-bold text-brand">Group Mentoring</p>
              <h3 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">7-Day IQMIZE Group Mentoring</h3>
              <p className="mt-4 text-4xl font-extrabold text-slate-900">₹4,900</p>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">
                A structured seven-day mentoring experience focusing on IQMIZE learning routines, memory practices,
                recall, visualization, practice and examination preparation.
              </p>
              <a
                href={waLink(WA_NUMBER_1, WA_GROUP_MSG)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="join-group-mentoring-btn"
                onClick={() => trackEvent("whatsapp_click", { source: "coaching-group", number: WA_NUMBER_1 })}
                className="mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-brand px-8 py-4 text-base font-bold text-white shadow-md transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-brand-dark hover:shadow-xl"
              >
                <MessageCircle size={19} /> Join Group Mentoring
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div data-testid="individual-mentoring-card" className="h-full rounded-3xl bg-inavy p-8 lg:p-10 shadow-[0_20px_40px_rgb(30,58,138,0.25)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_28px_50px_rgb(30,58,138,0.32)]">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-igold p-3">
                <User size={26} strokeWidth={1.8} />
              </div>
              <p className="mt-6 text-xs uppercase tracking-[0.2em] font-bold text-igold">Individual Mentoring</p>
              <h3 className="mt-2 text-2xl font-bold tracking-tight text-white">7-Day One-to-One Program</h3>
              <p className="mt-4 text-4xl font-extrabold text-white">₹19,000</p>
              <p className="mt-4 text-sm leading-relaxed text-sky-100/85">
                Personalized one-to-one mentoring designed around the student's learning process, practice and
                development.
              </p>
              <a
                href={waLink(WA_NUMBER_1, WA_INDIVIDUAL_MSG)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="enquire-individual-mentoring-btn"
                onClick={() => trackEvent("whatsapp_click", { source: "coaching-individual", number: WA_NUMBER_1 })}
                className="mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-igold px-8 py-4 text-base font-bold text-slate-900 shadow-md transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-igold-hover hover:shadow-xl"
              >
                <MessageCircle size={19} /> Enquire for Individual Mentoring
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="max-w-2xl mx-auto mt-16">
          <form
            onSubmit={submit}
            data-testid="enquiry-form"
            className="rounded-3xl border border-slate-200 bg-white p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.05)]"
          >
            <h3 className="text-xl font-bold tracking-tight text-slate-900">Have a question first?</h3>
            <p className="mt-2 text-sm text-slate-600">Leave your details and we'll get back to you.</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <input
                required
                data-testid="enquiry-name-input"
                className={inputCls}
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                required
                type="tel"
                data-testid="enquiry-phone-input"
                className={inputCls}
                placeholder="Phone / WhatsApp number"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <select
              data-testid="enquiry-interest-select"
              className={`${inputCls} mt-4`}
              value={form.interest}
              onChange={(e) => setForm({ ...form, interest: e.target.value })}
            >
              <option value="book">The IQMIZE Book — ₹49</option>
              <option value="group">Group Mentoring — ₹4,900</option>
              <option value="individual">Individual Mentoring — ₹19,000</option>
            </select>
            <textarea
              data-testid="enquiry-message-input"
              className={`${inputCls} mt-4 min-h-[96px] resize-y`}
              placeholder="Anything you'd like to ask? (optional)"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-form-submit-button"
              className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-inavy px-8 py-4 text-base font-bold text-white shadow-md transition-[transform,box-shadow,background-color,opacity] duration-300 hover:-translate-y-1 hover:bg-inavy-dark hover:shadow-xl disabled:opacity-60 disabled:hover:translate-y-0"
            >
              <Send size={18} /> {sending ? "Sending..." : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Coaching;
